import React, { useCallback, useState } from "react";
import dayjs from "dayjs";
import Button from "@mui/material/Button";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { API_get_weekly_labor_summary_report } from "../../../../../constants";
import { downloadCSV } from "../../../../../utilities/csvExport";
import WeekSelector from "../../../home/top_bar_components/WeekSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import ReportEmailToggle from "../ReportEmailToggle";
import {
  ReportScopeFilters,
  useCompanyReportScope,
  useScopedReportFetch,
} from "./_shared/companyReportScope";
import { lastCompletedBusinessDay } from "./_shared/reportDates";

// Weekly Labor Summary: the Weekly Labor Summary email in the library. Daily
// grades for the selected week as planned, and for the week before it as
// planned vs actual, with SPLH. The backend builds the grids with the same
// code as the email, so this file only lays them out.

function snapToWeekStart(date, weekStartDayIndex) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() - weekStartDayIndex + 7) % 7));
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

// The email's grade colors; planned grades for the previous week are faded.
const GRADE_COLORS = { A: "#059669", B: "#65a30d", C: "#f5be27", D: "#ea580c", F: "#dc2626" };
const FADED_GRADE_COLORS = { A: "#6bc9a8", B: "#a3c96e", C: "#f9d87b", D: "#f3a86e", F: "#ec7b7b" };

const money = (n) => `$${Math.round(Number(n || 0))}`;

function GradeCell({ grade, splh, faded }) {
  const known = GRADE_COLORS[grade];
  const background = known
    ? (faded ? FADED_GRADE_COLORS : GRADE_COLORS)[grade]
    : faded
    ? "#bfc8d4"
    : "#94a3b8";
  return (
    <td className="p-0.5">
      <div
        className="rounded-md text-white text-center py-1.5"
        style={{ backgroundColor: background }}
      >
        <div className="text-base font-bold leading-none">{known ? grade : "-"}</div>
        <div className="text-[11px] mt-1 opacity-90">
          {splh ? `${money(splh[0])} | ${money(splh[1])}` : "- | -"}
        </div>
      </div>
    </td>
  );
}

function GradeGrid({ title, legend, weekdays, weekStart, stores, kind }) {
  return (
    <section className="mb-8">
      <h4 className="text-base font-semibold text-slate-900">{title}</h4>
      <div className="text-xs text-slate-500 mt-0.5 mb-3 space-y-0.5">
        {legend.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
      <div className="overflow-x-auto">
        {/* Full width like the other reports' tables: a fixed store column
            and seven equal day columns that the grade cells fill. */}
        <table className="w-full min-w-[720px] table-fixed border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="w-[220px] text-left text-xs font-semibold text-slate-500 pr-3 pb-1">
                Store
              </th>
              {weekdays.map((day, i) => (
                <th key={day} className="text-xs font-semibold text-slate-500 pb-1 px-0.5">
                  {day.slice(0, 3)} {dayjs(addDays(weekStart, i)).format("M/D")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stores.map((store) => (
              <React.Fragment key={store.store_id}>
                {kind === "previous" && (
                  <tr>
                    <td className="text-sm text-slate-900 pr-3 align-middle" rowSpan={2}>
                      {store.store_name}
                    </td>
                    {store.previous.map((cell, i) => (
                      <GradeCell
                        key={i}
                        grade={cell?.planned_grade}
                        splh={cell && [cell.forecasted_splh, cell.scheduled_splh]}
                        faded
                      />
                    ))}
                  </tr>
                )}
                <tr>
                  {kind === "upcoming" && (
                    <td className="text-sm text-slate-900 pr-3 align-middle">{store.store_name}</td>
                  )}
                  {(kind === "upcoming" ? store.upcoming : store.previous).map((cell, i) =>
                    kind === "upcoming" ? (
                      <GradeCell
                        key={i}
                        grade={cell?.grade}
                        splh={cell && [cell.forecasted_splh, cell.scheduled_splh]}
                      />
                    ) : (
                      <GradeCell
                        key={i}
                        grade={cell?.actual_grade}
                        splh={cell && [cell.optimal_splh, cell.actual_splh]}
                      />
                    )
                  )}
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function WeeklyLaborSummaryReport({
  report,
  onBack,
  homeStore,
  homeCompany,
  storeNames,
  storeOptions: memberships,
  userId,
}) {
  const scope = useCompanyReportScope({
    report,
    homeStore,
    homeCompany,
    storeNames,
    memberships,
  });
  const { companyId, anchorStoreId, weekStartDayIndex, storeFilter } = scope;

  // Opens on the selected company's current business week, the one the email
  // grades as "upcoming" when it goes out at the start of the week. A picked
  // week is re-snapped to the selected company's week start.
  const currentBusinessDay = addDays(lastCompletedBusinessDay(storeNames, anchorStoreId), 1);
  const [pickedWeekStart, setPickedWeekStart] = useState(null);
  const weekStart = snapToWeekStart(
    pickedWeekStart ? addDays(pickedWeekStart, 3) : currentBusinessDay,
    weekStartDayIndex
  );

  const requestParams =
    homeStore && companyId
      ? {
          company_id: companyId,
          store_id: homeStore,
          week_start: dayjs(weekStart).format("YYYY-MM-DD"),
          ...(storeFilter.storeIds ? { store_ids: storeFilter.storeIds } : {}),
        }
      : null;
  const { payload, isLoading, error, retry } = useScopedReportFetch(
    API_get_weekly_labor_summary_report,
    requestParams,
    {
      onData: scope.acceptPayloadCompany,
      notFoundMessage: "You don't manage any stores with a weekly labor summary.",
      errorMessage: "Couldn't load the weekly labor summary. Try again.",
    }
  );

  const incrementWeek = useCallback((current, direction) => {
    setPickedWeekStart(addDays(current, direction * 7));
  }, []);
  const setWeekStartDate = useCallback(
    (newDate) => setPickedWeekStart(snapToWeekStart(newDate, weekStartDayIndex)),
    [weekStartDayIndex]
  );

  const filters = (
    <div className="flex flex-wrap items-center gap-3">
      <WeekSelector
        start_date={weekStart}
        end_date={addDays(weekStart, 6)}
        increment_week={incrementWeek}
        set_week_start_date={setWeekStartDate}
        businessWeekStartDayIndex={weekStartDayIndex}
        // "Today" is the week holding the selected company's current
        // business day.
        today={currentBusinessDay}
      />
      <ReportScopeFilters idPrefix="weekly-labor-summary" payload={payload} scope={scope} />
    </div>
  );

  const summary = payload?.summary || [];
  const weekdays = payload?.weekdays || [];
  const actions = (
    <>
      {payload?.csv && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={() =>
            downloadCSV(payload.csv, `Weekly_Labor_Summary_${payload.week_start}.csv`)
          }
          className="prebuilt-report-no-print"
          sx={{
            borderColor: "#6d28d9",
            color: "#6d28d9",
            textTransform: "none",
            whiteSpace: "nowrap",
            "&:hover": { borderColor: "#5b21b6", backgroundColor: "#f5f3ff" },
          }}
        >
          Download CSV
        </Button>
      )}
      <ReportEmailToggle reportId={report.id} userId={userId} />
    </>
  );

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      actions={actions}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={retry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : payload ? (
        summary.length ? (
          <div>
            <GradeGrid
              title={`Week starting ${dayjs(weekStart).format("ddd, MMM D, YYYY")}`}
              legend={["Planned grades. Forecasted SPLH | Scheduled SPLH"]}
              weekdays={weekdays}
              weekStart={weekStart}
              stores={summary}
              kind="upcoming"
            />
            <GradeGrid
              title={`Previous week starting ${dayjs(addDays(weekStart, -7)).format(
                "ddd, MMM D, YYYY"
              )}`}
              legend={[
                "Top row, faded: planned grades. Forecasted SPLH | Scheduled SPLH",
                "Bottom row: actual grades. Optimal SPLH | Actual SPLH",
              ]}
              weekdays={weekdays}
              weekStart={addDays(weekStart, -7)}
              stores={summary}
              kind="previous"
            />
          </div>
        ) : (
          <div className="text-sm text-gray-500 py-12 text-center border border-slate-200 rounded-lg">
            No weekly labor summary data for this week.
          </div>
        )
      ) : null}
    </PrebuiltReportShell>
  );
}
